import {
  AfterViewInit,
  Directive,
  ElementRef,
  HostListener,
  OnDestroy,
  Renderer2,
  inject,
} from '@angular/core';
import { NgControl } from '@angular/forms';
import { Subscription } from 'rxjs';

/**
 * Renders a live "123 / 500" counter underneath a textarea, so a truncated
 * paste is visible rather than silent. Opt-in: add `appCharCounter` to the
 * element. Reads its ceiling from the element's own `maxlength`, which
 * MaxLengthDirective supplies by default.
 */
@Directive({
  selector: 'textarea[appCharCounter]',
  standalone: true,
})
export class CharCounterDirective implements AfterViewInit, OnDestroy {
  private readonly el = inject<ElementRef<HTMLTextAreaElement>>(ElementRef);
  private readonly renderer = inject(Renderer2);
  private readonly ngControl = inject(NgControl, { optional: true, self: true });
  private counter: HTMLElement | null = null;
  private sub?: Subscription;
  private tracksControl = false;

  ngAfterViewInit(): void {
    this.counter = this.renderer.createElement('div');
    this.renderer.addClass(this.counter, 'form-text');
    this.renderer.addClass(this.counter, 'text-end');
    this.renderer.addClass(this.counter, 'text-muted');
    this.renderer.setAttribute(this.counter, 'aria-live', 'polite');
    const parent = this.renderer.parentNode(this.el.nativeElement);
    if (parent) {
      this.renderer.insertBefore(
        parent,
        this.counter,
        this.renderer.nextSibling(this.el.nativeElement)
      );
    }
    this.update();

    const control = this.ngControl?.control;
    if (control) {
      this.tracksControl = true;
      this.sub = control.valueChanges.subscribe(() => this.update());
    }
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
    const parent = this.counter && this.renderer.parentNode(this.counter);
    if (parent && this.counter) {
      this.renderer.removeChild(parent, this.counter);
    }
    this.counter = null;
  }

  @HostListener('input')
  onInput(): void {
    if (this.tracksControl) return;
    this.update();
  }

  private update(): void {
    if (!this.counter) return;
    const max = Number(this.el.nativeElement.getAttribute('maxlength'));

    if (!Number.isFinite(max) || max <= 0) {
      this.renderer.setStyle(this.counter, 'display', 'none');
      return;
    }
    this.renderer.removeStyle(this.counter, 'display');

    const length = this.el.nativeElement.value?.length ?? 0;
    this.renderer.setProperty(
      this.counter,
      'textContent',
      `${length} / ${max}`
    );

    // Warn as the user approaches the ceiling.
    const nearLimit = length >= max * 0.9;
    if (nearLimit) {
      this.renderer.addClass(this.counter, 'text-danger');
      this.renderer.removeClass(this.counter, 'text-muted');
    } else {
      this.renderer.removeClass(this.counter, 'text-danger');
      this.renderer.addClass(this.counter, 'text-muted');
    }
  }
}
